import React, { useState } from 'react';
import { Link } from 'react-router-dom';
// Api
import client from '../../api/client';
// Constants
import {
  BOOKING_ADMIN_PAGE_URL,
  RESET_PASS_PAGE_URL,
} from '../../utils/Routes';
// Constants
import { LOGIN_API } from '../../utils/ApiRoutes';
import LoadingSpinner from '../utils/LoadingSpinner';
// Context
import { useUser } from '../../context/UserContext';
// Hooks
import useNavigateToPage from '../../hooks/useNavigateToPage';
// Utils
import { setLocalToken, setSessionToken } from '../../utils/user/token';

function LoginForm() {
  const { setUser, setToken } = useUser();
  const navigateToPage = useNavigateToPage();

  const [loginInProgress, setLoginInProgress] = useState(false);
  const [loginError, setLoginError] = useState(null); // Set null initially for error message
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: '',
    keepMeLoggedIn: false,
  });

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginInProgress(true);
    setLoginError(null); // Reset error state

    client
      .post(LOGIN_API, loginFormData, false)
      .then((res) => {
        const token = res?.data?.token;
        const existingUser = res?.data?.existingUser;
        setToken(token);
        setUser(existingUser)
        
        // Persist per checkbox
        if (loginFormData.keepMeLoggedIn) {
          setLocalToken(token);
        } else {
          setSessionToken(token);
        }

        setLoginInProgress(false);
      })
      .then(() => navigateToPage(BOOKING_ADMIN_PAGE_URL))
      .catch((err) => {
        setLoginError(err.message);
        setLoginInProgress(false);
      });
  };

  const handleChange = (event) => {
    setLoginError(null); // Reset error state when user types
    const { name, value } = event.target;

    setLoginFormData({
      ...loginFormData,
      [name]: value,
    });
  };

  const handleCheckedKeepMeLoggedIn = () => {
    setLoginFormData({
      ...loginFormData,
      keepMeLoggedIn: !loginFormData.keepMeLoggedIn,
    });
  };

  return (
    <form className='space-y-4 md:space-y-6' onSubmit={handleLogin}>
      <div>
        <label
          htmlFor='email'
          className='block mb-2 text-sm font-medium text-colour9 dark:text-colour9'
        >
          Your email:
        </label>
        <input
          type='email'
          name='email'
          id='email'
          className='w-full p-2 mt-1 bg-colour7 dark:bg-colour7 rounded'
          placeholder='name@email.com'
          onChange={handleChange}
          required
          aria-invalid={loginError ? 'true' : 'false'}
          aria-describedby={loginError ? 'email-error' : undefined}
        />
      </div>
      <div>
        <label
          htmlFor='password'
          className='block mb-2 text-sm font-medium text-colour9 dark:text-colour9'
        >
          Password:
        </label>
        <input
          type='password'
          name='password'
          id='password'
          placeholder='••••••••'
          className='w-full p-2 mt-1 bg-colour7 dark:bg-colour7 rounded'
          required
          onChange={handleChange}
          aria-invalid={loginError ? 'true' : 'false'}
          aria-describedby={loginError ? 'password-error' : undefined}
        />
      </div>
      <div className='grid gap-2 items-center justify-between'>
        <div className='form-check flex justify-center mb-6'>
          <input
            className='form-check-input h-4 w-4 border border-gray-300 rounded-sm transition duration-200 mt-1 align-top float-left mr-2 cursor-pointer'
            type='checkbox'
            id='keepMeLoggedIn'
            name='keepMeLoggedIn'
            checked={loginFormData.keepMeLoggedIn}
            onChange={handleCheckedKeepMeLoggedIn}
            aria-label='Keep me logged in'
            style={{ accentColor: '#ff0000' }} // Using `accent-color` here
          />
          <label
            className='form-check-label inline-block text-colour9'
            htmlFor='keepMeLoggedIn'
          >
            Keep me logged in
          </label>
        </div>
      </div>
      <div className='grid justify-center'>
        <Link
          to={RESET_PASS_PAGE_URL}
          className='text-sm font-medium text-colour2 hover:underline'
          aria-label='Forgot password?'
        >
          Forgot password?
        </Link>
      </div>

      <div>
        <button
          type='submit'
          className={`inline-block px-6 py-2.5 w-full bg-colour4 text-white font-medium text-xs leading-tight uppercase rounded shadow-md hover:brightness-110 hover:shadow-lg focus:bg-colour4 focus:shadow-lg focus:outline-none focus:ring-0 active:bg-colour4 active:shadow-lg transition duration-150 ease-in-out`}
          aria-label='Sign in to your account'
          disabled={loginInProgress}
        >
          {loginInProgress ? (
            <div className='flex justify-center'>
              <LoadingSpinner sm={true} />
            </div>
          ) : (
            <span>Sign in</span>
          )}
        </button>
      </div>

      {loginError && (
        <div
          role='alert'
          aria-live='assertive'
          className='text-error-red text-center'
        >
          {loginError || 'Unable to login. Please check your credentials.'}
        </div>
      )}
    </form>
  );
}

export default LoginForm;
